import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Compass, Radio, Search, Building2, Map as MapIcon,
  RefreshCw, ChevronRight, Zap, CheckCircle2, AlertTriangle,
  XCircle, Clock, MapPin, Globe, Send, Shield, Users, Filter,
  Sparkles, ArrowRight, Brain, Puzzle, TrendingUp, TrendingDown,
  BarChart3, HeartHandshake, Mail, DollarSign,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, LayerGroup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, API_BASE } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import {
  PageShell, PageHeader, Card, Section, Pill,
  PrimaryButton, GhostButton, TabsShell, Metric,
  NAVY, NAVY_DARK, CHAMPAGNE, GOLD, IVORY, SLATE,
} from "@/components/altiora/PremiumUI";

const GREEN = "#0B7A3B";
const AMBER = "#B45309";
const CRIMSON = "#B91C1C";
const CREAM = IVORY;

/* Fix Leaflet default icon URLs (bundler quirk) */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const LAYER_COLORS = {
  visits:       "#0B7A3B",
  prospects:    "#C5A880",
  partners:     "#0B2545",
  members:      "#F5D687",
  institutions: "#7C3AED",
};
const LAYER_LABELS = {
  visits:       "Visites terrain",
  prospects:    "Prospects",
  partners:     "Partenaires",
  members:      "Membres ALTIORA",
  institutions: "Institutions",
};

const dotIcon = (color) => L.divIcon({
  className: "custom-dot",
  html: `<div style="width:14px;height:14px;border-radius:50%;
                     background:${color};border:2px solid #fff;
                     box-shadow:0 0 0 1px rgba(0,0,0,.25);"></div>`,
  iconSize: [14, 14], iconAnchor: [7, 7],
});

/* ══════════════════════════════════════════════════════════════════
   SYNC DASHBOARD — real-time via SSE
   ══════════════════════════════════════════════════════════════════ */
const STATUS_META = {
  healthy:         { color: GREEN,   icon: CheckCircle2, fr: "OK" },
  attention:       { color: AMBER,   icon: AlertTriangle, fr: "Attention" },
  idle:            { color: "#94A3B8", icon: Clock,      fr: "Inactif" },
  disconnected:    { color: "#94A3B8", icon: XCircle,    fr: "Non connecté" },
  config_pending:  { color: AMBER,   icon: AlertTriangle, fr: "Config requise" },
  error:           { color: CRIMSON, icon: XCircle,     fr: "Erreur" },
};

function SyncDashboard() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    let es = null; let mounted = true;
    // Fallback initial fetch
    api.get("/intel/sync/health").then(r => mounted && setData(r.data));
    // SSE stream
    try {
      es = new EventSource(`${API_BASE}/intel/sync/stream`,
                                { withCredentials: true });
      es.addEventListener("health", (e) => {
        if (!mounted) return;
        try { setData(JSON.parse(e.data)); setLive(true); } catch {}
      });
      es.onerror = () => setLive(false);
    } catch (e) { setLive(false); }
    return () => { mounted = false; es?.close(); };
  }, []);

  if (!data) return <div className="p-6"><Loader2 size={16} className="animate-spin" /></div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="sync-dashboard">
      <div className="flex items-center gap-2 mb-4">
        <Radio size={16} style={{ color: live ? GREEN : "#94A3B8" }}
                className={live ? "animate-pulse" : ""} />
        <h3 className="font-serif text-lg" style={{ color: NAVY }}>Santé des connecteurs</h3>
        <span className="text-[10px] uppercase tracking-widest ml-auto"
              style={{ color: live ? GREEN : "#94A3B8" }} data-testid="sync-live-indicator">
          {live ? "Temps réel" : "Snapshot"}
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {data.connectors.map(c => {
          const m = STATUS_META[c.status] || STATUS_META.idle;
          const I = m.icon;
          return (
            <div key={c.id} className="rounded-lg p-3 border"
                 style={{ borderColor: m.color + "55",
                            background: m.color + "0A" }}
                 data-testid={`sync-row-${c.id}`}>
              <div className="flex items-start gap-2">
                <I size={14} style={{ color: m.color }} className="mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold" style={{ color: NAVY }}>
                      {c.label_fr}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded uppercase tracking-widest"
                          style={{ background: m.color + "22", color: m.color }}>
                      {m.fr}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {c.count_synced ?? 0} élément(s)
                    {c.last_sync_at && <> · {new Date(c.last_sync_at).toLocaleString("fr-CA")}</>}
                  </div>
                  {c.recommended_fr && (
                    <div className="text-[11px] mt-1" style={{ color: m.color }}>
                      → {c.recommended_fr}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FIELD MAP — Leaflet with filterable layers
   ══════════════════════════════════════════════════════════════════ */
function FieldMap() {
  const [layers, setLayers] = useState(null);
  const [enabled, setEnabled] = useState({
    visits: true, prospects: true, partners: true,
    members: true, institutions: true,
  });

  useEffect(() => {
    api.get("/intel/map/layers")
       .then(r => setLayers(r.data))
       .catch(() => toast.error("Erreur de chargement carte."));
  }, []);

  const points = useMemo(() => {
    if (!layers) return [];
    return Object.entries(layers.layers)
      .flatMap(([k, arr]) => enabled[k] ? arr : []);
  }, [layers, enabled]);

  const center = points.length
    ? [points[0].lat, points[0].lng]
    : [46.8033, -71.1779]; // Lévis, Québec default

  if (!layers) return <div className="p-6"><Loader2 size={16} className="animate-spin" /></div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid="field-map">
      <div className="p-4 flex items-center gap-2 border-b">
        <MapIcon size={16} style={{ color: CHAMPAGNE }} />
        <h3 className="font-serif text-lg" style={{ color: NAVY }}>Carte du territoire</h3>
        <span className="text-[10px] uppercase tracking-widest ml-auto" style={{ color: CHAMPAGNE }}>
          {points.length} point(s) affiché(s)
        </span>
      </div>

      {/* Layer filters */}
      <div className="p-3 flex flex-wrap gap-1.5 border-b" data-testid="field-map-filters">
        {Object.keys(LAYER_LABELS).map(k => {
          const on = enabled[k];
          const count = layers.counts[k] || 0;
          return (
            <button key={k} onClick={() => setEnabled(e => ({ ...e, [k]: !e[k] }))}
                    data-testid={`field-map-filter-${k}`}
                    className="text-[11px] px-2.5 py-1 rounded-full transition flex items-center gap-1.5"
                    style={{ background: on ? LAYER_COLORS[k] : "#F1F5F9",
                               color: on ? "#fff" : "#334155",
                               fontWeight: on ? 600 : 500 }}>
              <span className="w-2 h-2 rounded-full"
                    style={{ background: on ? "#fff" : LAYER_COLORS[k] }} />
              {LAYER_LABELS[k]} <span className="opacity-70">({count})</span>
            </button>
          );
        })}
      </div>

      <div style={{ height: 480 }} data-testid="field-map-canvas">
        <MapContainer center={center} zoom={6} style={{ height: "100%", width: "100%" }}
                          scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://osm.org">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {Object.entries(layers.layers).map(([k, arr]) => enabled[k] && (
            <LayerGroup key={k}>
              {arr.map(p => (
                <Marker key={`${k}-${p.id}`} position={[p.lat, p.lng]}
                          icon={dotIcon(LAYER_COLORS[k])}>
                  <Popup>
                    <div style={{ fontSize: 12, minWidth: 160 }}>
                      <div style={{ fontWeight: 600, color: NAVY }}>{p.label}</div>
                      {p.sublabel && <div style={{ color: "#64748B" }}>{p.sublabel}</div>}
                      {p.value_cad > 0 && (
                        <div style={{ color: GREEN, marginTop: 4 }}>
                          {p.value_cad.toLocaleString("fr-CA")} $ CAD
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              ))}
            </LayerGroup>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   PUBLIC REGISTRIES — international catalog + search
   ══════════════════════════════════════════════════════════════════ */
function RegistriesPanel() {
  const [catalog, setCatalog] = useState(null);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const load = () => api.get("/intel/registries").then(r => setCatalog(r.data));
  useEffect(() => { load(); }, []);

  const search = async () => {
    if (!selected || !query.trim() || query.trim().length < 2) return;
    setBusy(true); setResults(null);
    try {
      const r = await api.post(`/intel/registries/${selected.id}/search`,
                                    { query, limit: 20 });
      setResults(r.data);
      toast.success(`${r.data.imported} résultat(s) importé(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Registre indisponible.");
    }
    setBusy(false);
  };

  if (!catalog) return <div className="p-6"><Loader2 size={16} className="animate-spin" /></div>;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="registries-panel">
      <div className="flex items-center gap-2 mb-3">
        <Globe size={16} style={{ color: CHAMPAGNE }} />
        <h3 className="font-serif text-lg" style={{ color: NAVY }}>Registres publics · International</h3>
        <button onClick={() => setWizardOpen(v => !v)}
                data-testid="registries-open-wizard-btn"
                className="text-[11px] ml-auto px-3 py-1 rounded"
                style={{ background: CHAMPAGNE, color: NAVY_DARK, fontWeight: 600 }}>
          {wizardOpen ? "Fermer l'assistant" : "+ Ajouter un pays"}
        </button>
      </div>
      {wizardOpen && (
        <div className="mb-4">
          <CountryWizard onCreated={() => { setWizardOpen(false); load(); }} />
        </div>
      )}
      <p className="text-[11px] text-slate-500 mb-4 max-w-2xl">
        Architecture modulaire — chaque pays est un module indépendant. Sélectionnez
        un registre, lancez une recherche, les résultats passent par le même pipeline
        de scoring et rejoignent vos opportunités.
      </p>

      {/* Catalog grouped by country */}
      <div className="space-y-2 mb-4">
        {Object.entries(catalog.by_country).map(([country, regs]) => (
          <div key={country} data-testid={`registries-country-${country}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              {country}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {regs.map(r => (
                <button key={r.id} onClick={() => setSelected(r)}
                        data-testid={`registry-${r.id}`}
                        className="text-left rounded-lg p-3 border transition hover:brightness-95"
                        style={{ borderColor: selected?.id === r.id ? NAVY : "#E5E7EB",
                                   background: selected?.id === r.id ? CREAM : "#fff" }}>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold" style={{ color: NAVY }}>
                      {r.label_fr}
                    </span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded ml-auto"
                          style={{ background: r.status === "available" ? GREEN + "22" :
                                                    r.status === "beta"   ? GOLD + "44" :
                                                    "#F1F5F9",
                                     color: r.status === "available" ? GREEN :
                                              r.status === "beta"   ? "#B45309" : "#64748B" }}>
                      {r.status_fr}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {r.regulator} · {r.scope_fr}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Search bar */}
      {selected && (
        <div className="border-t pt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
            Recherche dans « {selected.label_fr} »
          </div>
          {selected.status === "coming_soon" && (
            <div className="text-[11px] p-2 rounded mb-2" data-testid="registry-coming-soon-hint"
                 style={{ background: "#FEF3C7", color: AMBER }}>
              <AlertTriangle size={11} className="inline mb-0.5" /> Ce connecteur
              est en cours d'intégration. La recherche live sera activée dès validation
              du régulateur du pays concerné.
            </div>
          )}
          <div className="flex gap-2">
            <input value={query} onChange={e => setQuery(e.target.value)}
                     onKeyDown={e => e.key === "Enter" && search()}
                     placeholder="Nom d'entreprise, secteur, mot-clé…"
                     data-testid="registry-search-input"
                     disabled={selected.status === "coming_soon"}
                     className="flex-1 text-[13px] p-2 rounded border disabled:bg-slate-100"
                     style={{ borderColor: CHAMPAGNE + "55" }} />
            <button onClick={search}
                    disabled={busy || query.trim().length < 2 || selected.status === "coming_soon"}
                    data-testid="registry-search-btn"
                    className="text-[12px] px-4 py-2 rounded flex items-center gap-1.5 disabled:opacity-50"
                    style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
              Rechercher
            </button>
          </div>

          {results && (
            <div className="mt-3 space-y-1" data-testid="registry-search-results">
              <div className="text-[11px] text-slate-500">
                {results.imported} importé(s) · {results.updated || 0} mis à jour
              </div>
              {results.results.map((r, i) => (
                <div key={i} className="p-2 rounded text-[12px] flex items-center gap-2"
                     style={{ background: CREAM }}>
                  <ArrowRight size={11} style={{ color: CHAMPAGNE }} />
                  <span className="font-semibold" style={{ color: NAVY }}>{r.name}</span>
                  <span className="text-slate-500">· {r.sector_fr} · {r.city}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   INSTITUTIONAL PORTAL — inline register + admin queue
   ══════════════════════════════════════════════════════════════════ */
function InstitutionalPortalPanel() {
  const [me, setMe] = useState(null);
  const [pending, setPending] = useState(null);
  const [available, setAvailable] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const refresh = async () => {
    const [m, p, a] = await Promise.all([
      api.get("/institutional-portal/mine"),
      api.get("/institutional-portal/admin/pending").catch(() => ({ data: null })),
      api.get("/institutional-portal/requests/available").catch(() => ({ data: null })),
    ]);
    setMe(m.data); setPending(p.data); setAvailable(a.data);
  };
  useEffect(() => { refresh(); }, []);

  const decide = async (iid, action) => {
    try {
      await api.post(`/institutional-portal/admin/${iid}/decision`,
                          { action, reason_fr: action === "reject"
                                                     ? "Non conforme aux critères ALTIORA." : null });
      toast.success(action === "approve" ? "Approuvé" : "Refusé");
      await refresh();
    } catch { toast.error("Impossible."); }
  };

  return (
    <div className="space-y-4">
      {/* My institutions */}
      <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="institutions-mine">
        <div className="flex items-center gap-2 mb-3">
          <Building2 size={16} style={{ color: CHAMPAGNE }} />
          <h3 className="font-serif text-lg" style={{ color: NAVY }}>Mes institutions</h3>
          <button onClick={() => setFormOpen(v => !v)}
                  data-testid="institutions-register-toggle"
                  className="text-[11px] ml-auto px-3 py-1.5 rounded-lg"
                  style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
            {formOpen ? "Fermer" : "+ Enregistrer une institution"}
          </button>
        </div>
        {formOpen && <RegisterForm types={me?.types || []} onCreated={() => { setFormOpen(false); refresh(); }} />}
        {!me || me.institutions.length === 0 ? (
          <div className="text-[12px] text-slate-500 italic">Aucune institution enregistrée.</div>
        ) : (
          <div className="space-y-2">
            {me.institutions.map(i => (
              <div key={i.id} className="rounded-lg p-3 flex items-center gap-3"
                   style={{ background: i.status === "approved" ? CREAM : "#F8FAFC" }}
                   data-testid={`institutions-mine-${i.id}`}>
                <Building2 size={14} style={{ color: NAVY }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{i.name}</div>
                  <div className="text-[11px] text-slate-500">{i.org_type} · {i.country_name_fr}</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-widest"
                      style={{ background: i.status === "approved" ? GREEN + "22" :
                                             i.status === "rejected" ? CRIMSON + "22" :
                                             "#FEF3C7",
                                 color: i.status === "approved" ? GREEN :
                                          i.status === "rejected" ? CRIMSON : "#B45309" }}>
                  {i.status === "approved" ? "Approuvé" :
                    i.status === "rejected" ? "Refusé" : "En attente"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin queue */}
      {pending && (
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="institutions-admin">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={16} style={{ color: CHAMPAGNE }} />
            <h3 className="font-serif text-lg" style={{ color: NAVY }}>Validation admin</h3>
            <span className="text-[10px] uppercase tracking-widest ml-auto"
                  style={{ color: CHAMPAGNE }}>
              {pending.count} en attente
            </span>
          </div>
          {pending.institutions.length === 0 ? (
            <div className="text-[12px] text-slate-500 italic">Aucune demande en attente.</div>
          ) : (
            <div className="space-y-2">
              {pending.institutions.map(i => (
                <div key={i.id} className="rounded-lg p-3 border"
                     style={{ borderColor: "#E5E7EB" }}
                     data-testid={`institutions-admin-${i.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
                        {i.name}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {i.org_type} · {i.country_name_fr} · {i.contact_email}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1">{i.description_fr}</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => decide(i.id, "approve")}
                              data-testid={`institutions-admin-approve-${i.id}`}
                              className="text-[11px] px-3 py-1.5 rounded"
                              style={{ background: GREEN, color: "#fff", fontWeight: 600 }}>
                        Approuver
                      </button>
                      <button onClick={() => decide(i.id, "reject")}
                              data-testid={`institutions-admin-reject-${i.id}`}
                              className="text-[11px] px-3 py-1.5 rounded"
                              style={{ background: "#FEF2F2", color: CRIMSON }}>
                        Refuser
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Available requests for pros */}
      {available && available.count > 0 && (
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="institutions-available">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={16} style={{ color: CHAMPAGNE }} />
            <h3 className="font-serif text-lg" style={{ color: NAVY }}>
              Demandes d'accompagnement disponibles
            </h3>
          </div>
          <div className="space-y-2">
            {available.requests.map(r => (
              <div key={r.id} className="rounded-lg p-3"
                   style={{ background: CREAM }}
                   data-testid={`institutions-request-${r.id}`}>
                <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{r.title_fr}</div>
                <div className="text-[11px] text-slate-500">
                  {r.institution_name} · {r.territory_fr || "Territoire ouvert"} · urgence {r.urgency}
                </div>
                <div className="text-[12px] mt-1">{r.description_fr.slice(0, 200)}…</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RegisterForm({ types, onCreated }) {
  const [f, setF] = useState({
    name: "", org_type: types[0] || "Autre", country_name_fr: "Québec (Canada)",
    city: "", contact_name: "", contact_email: "", description_fr: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    if (!f.name || !f.contact_email || !f.description_fr) {
      toast.error("Nom, courriel et description sont requis."); return;
    }
    setBusy(true);
    try {
      await api.post("/institutional-portal/register", f);
      toast.success("Institution soumise pour validation");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-lg p-3 mb-3" style={{ background: CREAM }}
         data-testid="institutions-register-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
        <input value={f.name} onChange={e => set("name", e.target.value)}
                 placeholder="Nom de l'institution"
                 data-testid="institutions-register-name"
                 className="p-2 rounded border" style={{ borderColor: CHAMPAGNE + "55" }} />
        <select value={f.org_type} onChange={e => set("org_type", e.target.value)}
                  data-testid="institutions-register-type"
                  className="p-2 rounded border" style={{ borderColor: CHAMPAGNE + "55" }}>
          {(types.length ? types : ["Autre"]).map(t => <option key={t}>{t}</option>)}
        </select>
        <input value={f.country_name_fr} onChange={e => set("country_name_fr", e.target.value)}
                 placeholder="Pays" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <input value={f.city} onChange={e => set("city", e.target.value)}
                 placeholder="Ville" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <input value={f.contact_name} onChange={e => set("contact_name", e.target.value)}
                 placeholder="Nom du contact" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <input value={f.contact_email} onChange={e => set("contact_email", e.target.value)}
                 placeholder="Courriel du contact" type="email"
                 data-testid="institutions-register-email"
                 className="p-2 rounded border" style={{ borderColor: CHAMPAGNE + "55" }} />
        <textarea value={f.description_fr} onChange={e => set("description_fr", e.target.value)}
                    placeholder="Description de l'institution et de ses missions…" rows={3}
                    className="md:col-span-2 p-2 rounded border resize-none"
                    style={{ borderColor: CHAMPAGNE + "55" }} />
      </div>
      <button onClick={submit} disabled={busy}
              data-testid="institutions-register-submit"
              className="mt-2 text-[12px] px-4 py-2 rounded disabled:opacity-50"
              style={{ background: NAVY, color: "#fff", fontWeight: 600 }}>
        {busy ? <Loader2 size={12} className="animate-spin inline mr-1" /> : null}
        Soumettre pour validation
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   LEARNING LOOP — Copilot summary of what the AI has learned
   ══════════════════════════════════════════════════════════════════ */
function LearningLoopPanel() {
  const [sum, setSum] = useState(null);
  const [sil, setSil] = useState(null);
  useEffect(() => {
    api.get("/intel/learning/summary").then(r => setSum(r.data)).catch(() => {});
    api.get("/intel/silence/summary").then(r => setSil(r.data)).catch(() => {});
  }, []);
  if (!sum) return null;

  return (
    <div className="space-y-4">
      <Card className="p-5" testid="learning-loop-panel">
        <Section icon={Brain} title="Ce que votre Copilote a appris"
                   hint={`${sum.events_recorded} arbitrage(s) enregistré(s) — le score ajuste automatiquement les prochaines recommandations.`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          <div data-testid="learning-boosted">
            <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: GREEN }}>
              <TrendingUp size={10} className="inline mb-0.5" /> Recommandations amplifiées
            </div>
            {sum.boosted.length === 0 ? (
              <div className="text-[11px] italic" style={{ color: SLATE }}>
                Aucune préférence détectée pour l'instant.
              </div>
            ) : (
              <div className="space-y-1">
                {sum.boosted.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px]">
                    <Pill tone="green">+{b.delta}</Pill>
                    <span style={{ color: NAVY }}>{b.value}</span>
                    <span className="text-[10px]" style={{ color: SLATE }}>
                      · {b.feature}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div data-testid="learning-penalised">
            <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: CRIMSON }}>
              <TrendingDown size={10} className="inline mb-0.5" /> Recommandations atténuées
            </div>
            {sum.penalised.length === 0 ? (
              <div className="text-[11px] italic" style={{ color: SLATE }}>
                Aucune pénalité pour l'instant.
              </div>
            ) : (
              <div className="space-y-1">
                {sum.penalised.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px]">
                    <Pill tone="red">{b.delta}</Pill>
                    <span style={{ color: NAVY }}>{b.value}</span>
                    <span className="text-[10px]" style={{ color: SLATE }}>
                      · {b.feature}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Section>
    </Card>

    {sil && (
      <Card className="p-5" testid="silence-panel">
        <Section icon={Clock} title="Apprentissage par le silence"
                   hint={`Fenêtre de silence : ${sil.silence_window_days} jours. Le Copilote détecte les opportunités que vous consultez mais ne relancez jamais, et ajuste discrètement les recommandations similaires.`}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            <Metric label="Vues totales" value={sil.total_views} testid="silence-views" />
            <Metric label="Engagées"     value={sil.engaged}     accent={GREEN} testid="silence-engaged" />
            <Metric label="Pénalisées"   value={sil.silently_penalised} accent={CRIMSON} testid="silence-penalised" />
            <Metric label="En attente"   value={sil.pending_decay} testid="silence-pending" />
          </div>
        </Section>
      </Card>
    )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   CONNECTORS FRAMEWORK — capabilities matrix
   ══════════════════════════════════════════════════════════════════ */
function ConnectorsFrameworkPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/intel/connectors-framework").then(r => setData(r.data));
  }, []);
  if (!data) return <div className="p-6"><Loader2 size={16} className="animate-spin" /></div>;

  const CATEGORY_LABELS = {
    registry: "Registres publics", internal: "Sources internes",
    field:    "Prospection terrain", oauth: "Connecteurs OAuth",
    community: "Réseau communautaire", partner: "Partenaires & institutions",
    government: "API gouvernementales", social: "Réseaux sociaux",
    bank:     "Banques", university: "Universités", other: "Autres",
  };

  return (
    <div className="space-y-4" data-testid="connectors-framework-panel">
      <Card className="p-5">
        <Section icon={Puzzle} title="Universal Connector Framework"
                   hint={`${data.count} connecteurs enregistrés — toute nouvelle source se branche via ce contrat, sans modifier le cœur.`}>
          <div className="text-[11px] mt-2 flex flex-wrap gap-1.5">
            {data.capabilities_matrix.map(c => <Pill key={c} tone="navy">{c}</Pill>)}
          </div>
        </Section>
      </Card>

      {Object.entries(data.by_category).map(([cat, conns]) => (
        <Card key={cat} className="p-5" testid={`connectors-cat-${cat}`}>
          <div className="text-[10px] uppercase tracking-widest mb-2"
               style={{ color: CHAMPAGNE }}>
            {CATEGORY_LABELS[cat] || cat} · {conns.length}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {conns.map(c => (
              <div key={c.id} className="rounded-lg p-3 border"
                   style={{ borderColor: `${CHAMPAGNE}22`, background: "#fff" }}
                   data-testid={`connector-${c.id}`}>
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold" style={{ color: NAVY }}>
                    {c.label_fr}
                  </span>
                  <Pill tone={c.status === "available" ? "green" :
                                 c.status === "beta"       ? "amber" : "neutral"}>
                    {c.status_fr}
                  </Pill>
                  {c.consent_required && <Pill tone="navy">Consentement</Pill>}
                </div>
                <div className="text-[11px] mt-1" style={{ color: SLATE }}>
                  {c.country_name_fr} · {c.rate_limit_per_min}/min
                </div>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   COUNTRY ONBOARDING WIZARD — admins add a new registry in 3 steps
   ══════════════════════════════════════════════════════════════════ */
function CountryWizard({ onCreated }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [draft, setDraft] = useState({
    country_code: "", country_name_fr: "", label_fr: "",
    regulator: "", scope_fr: "",
    search_url_template: "", response_shape: "json",
    field_mapping: {},
    consent_required: false, rate_limit_per_min: 30,
  });
  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const setMap = (k, v) => setDraft(d => ({
    ...d, field_mapping: { ...d.field_mapping, [k]: v } }));

  const validate = async () => {
    setBusy(true); setPreview(null);
    try {
      const r = await api.post("/intel/country-wizard/validate",
                                    { draft, sample_query: "test" });
      setPreview(r.data);
      toast.success(`Test réussi — ${r.data.count} ligne(s) analysée(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test échoué.");
    }
    setBusy(false);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/intel/country-wizard/submit", { draft });
      toast.success(r.data.message_fr);
      onCreated?.();
      setStep(1); setPreview(null);
      setDraft({ country_code: "", country_name_fr: "", label_fr: "",
                    regulator: "", scope_fr: "", search_url_template: "",
                    response_shape: "json", field_mapping: {},
                    consent_required: false, rate_limit_per_min: 30 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    }
    setBusy(false);
  };

  const canNext = {
    1: draft.country_code && draft.country_name_fr && draft.label_fr &&
        draft.regulator && draft.scope_fr,
    2: draft.search_url_template.length >= 8,
    3: Object.keys(draft.field_mapping).length > 0,
  }[step];

  return (
    <Card className="p-5" testid="country-wizard">
      <Section icon={Sparkles} title="Assistant Nouveau Pays"
                 hint={`Étape ${step} / 3 — définissez un connecteur pays sans écrire de code.`}>
        {/* Progress */}
        <div className="flex gap-2 mb-4 mt-3">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex-1 h-1 rounded-full transition"
                 style={{ background: s <= step ? CHAMPAGNE : "#E5E7EB" }} />
          ))}
        </div>

        {step === 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]"
               data-testid="wizard-step1">
            <input value={draft.country_code} onChange={e => set("country_code", e.target.value.toUpperCase())}
                     placeholder="Code pays ISO (FR, CA-QC…) *"
                     data-testid="wizard-country-code"
                     className="p-2 rounded border" style={{ borderColor: CHAMPAGNE + "55" }} />
            <input value={draft.country_name_fr} onChange={e => set("country_name_fr", e.target.value)}
                     placeholder="Nom du pays *"
                     data-testid="wizard-country-name"
                     className="p-2 rounded border"
                     style={{ borderColor: CHAMPAGNE + "55" }} />
            <input value={draft.label_fr} onChange={e => set("label_fr", e.target.value)}
                     placeholder="Nom du registre *"
                     data-testid="wizard-label"
                     className="p-2 rounded border md:col-span-2"
                     style={{ borderColor: CHAMPAGNE + "55" }} />
            <input value={draft.regulator} onChange={e => set("regulator", e.target.value)}
                     placeholder="Régulateur / autorité *"
                     data-testid="wizard-regulator"
                     className="p-2 rounded border md:col-span-2"
                     style={{ borderColor: CHAMPAGNE + "55" }} />
            <textarea value={draft.scope_fr} onChange={e => set("scope_fr", e.target.value)}
                        placeholder="Portée du registre *" rows={2}
                        data-testid="wizard-scope"
                        className="p-2 rounded border md:col-span-2 resize-none"
                        style={{ borderColor: CHAMPAGNE + "55" }} />
          </div>
        )}

        {step === 2 && (
          <div className="space-y-2" data-testid="wizard-step2">
            <input value={draft.search_url_template}
                     onChange={e => set("search_url_template", e.target.value)}
                     placeholder="URL de recherche (utilise {q} pour la requête)"
                     data-testid="wizard-url"
                     className="w-full p-2 rounded border text-[12px]"
                     style={{ borderColor: CHAMPAGNE + "55" }} />
            <div className="text-[10px] italic" style={{ color: SLATE }}>
              Ex. https://api.example.com/search?nom={"{q}"}
            </div>
            <select value={draft.response_shape} onChange={e => set("response_shape", e.target.value)}
                      data-testid="wizard-shape"
                      className="text-[12px] p-2 rounded border"
                      style={{ borderColor: CHAMPAGNE + "55" }}>
              <option value="json">Réponse JSON</option>
              <option value="html_table">Tableau HTML</option>
              <option value="csv">CSV</option>
            </select>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2" data-testid="wizard-step3">
            <div className="text-[11px]" style={{ color: SLATE }}>
              Mappez les champs ALTIORA vers votre réponse.
              {draft.response_shape === "json" && " Utilisez des chemins pointés (ex. address.city)."}
              {draft.response_shape === "html_table" && " Indiquez l'index de colonne (0, 1, 2…)."}
              {draft.response_shape === "csv" && " Indiquez le nom exact de la colonne."}
            </div>
            {["name", "email", "phone", "company", "sector_fr", "city"].map(f => (
              <div key={f} className="flex items-center gap-2">
                <label className="text-[11px] w-20" style={{ color: NAVY }}>{f}</label>
                <input value={draft.field_mapping[f] || ""}
                         onChange={e => setMap(f, e.target.value)}
                         data-testid={`wizard-map-${f}`}
                         placeholder="…" className="flex-1 p-1.5 rounded border text-[12px]"
                         style={{ borderColor: CHAMPAGNE + "55" }} />
              </div>
            ))}
            {preview && (
              <div className="mt-3 p-2 rounded" style={{ background: IVORY }}
                   data-testid="wizard-preview">
                <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: CHAMPAGNE }}>
                  Aperçu · {preview.count} ligne(s)
                </div>
                {preview.rows.slice(0, 3).map((r, i) => (
                  <div key={i} className="text-[11px]" style={{ color: NAVY }}>
                    → {r.name || r.company || "?"} · {r.email || ""} · {r.city || ""}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-4 pt-3 border-t">
          {step > 1 && (
            <GhostButton onClick={() => setStep(s => s - 1)}
                            testid="wizard-prev-btn">
              Précédent
            </GhostButton>
          )}
          {step < 3 && (
            <PrimaryButton onClick={() => setStep(s => s + 1)}
                              disabled={!canNext}
                              testid="wizard-next-btn">
              Suivant →
            </PrimaryButton>
          )}
          {step === 3 && (
            <>
              <GhostButton onClick={validate} disabled={busy || !canNext}
                              testid="wizard-validate-btn">
                {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                Tester
              </GhostButton>
              <PrimaryButton onClick={submit}
                                disabled={busy || !canNext || !preview}
                                testid="wizard-submit-btn">
                Activer le registre
              </PrimaryButton>
            </>
          )}
        </div>
      </Section>
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MARKETPLACE — Community-submitted plugin connectors
   ══════════════════════════════════════════════════════════════════ */
function MarketplacePanel() {
  const [mine, setMine] = useState(null);
  const [pending, setPending] = useState(null);
  const [activated, setActivated] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const refresh = async () => {
    const [m, p, a] = await Promise.all([
      api.get("/intel/marketplace/mine"),
      api.get("/intel/marketplace/admin/pending").catch(() => ({ data: null })),
      api.get("/intel/marketplace/activated"),
    ]);
    setMine(m.data); setPending(p.data); setActivated(a.data);
  };
  useEffect(() => { refresh(); }, []);

  const review = async (pid, action) => {
    try {
      await api.post(`/intel/marketplace/admin/${pid}/review`, { action });
      toast.success(`Action « ${action} » appliquée`);
      await refresh();
    } catch { toast.error("Impossible."); }
  };

  return (
    <div className="space-y-4" data-testid="marketplace-panel">
      <Card className="p-5">
        <Section icon={Puzzle} title="Marketplace des connecteurs"
                   hint="Proposez un nouveau connecteur — l'équipe ALTIORA valide manuellement chaque soumission avant activation. Les connecteurs approuvés apparaissent automatiquement dans le Framework."
                   actions={[
                     <PrimaryButton key="s" onClick={() => setFormOpen(v => !v)}
                                       testid="marketplace-submit-toggle">
                       {formOpen ? "Fermer" : "+ Soumettre"}
                     </PrimaryButton>,
                   ]}>
          {formOpen && <MarketplaceSubmitForm onCreated={() => { setFormOpen(false); refresh(); }} />}
        </Section>
      </Card>

      {activated && activated.count > 0 && (
        <Card className="p-5" testid="marketplace-activated">
          <div className="text-[10px] uppercase tracking-widest mb-2"
               style={{ color: CHAMPAGNE }}>
            Connecteurs actifs · {activated.count}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {activated.plugins.map(p => (
              <div key={p.id} className="rounded-lg p-3 border"
                   style={{ borderColor: GREEN + "44", background: GREEN + "0A" }}>
                <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{p.label_fr}</div>
                <div className="text-[11px] mt-0.5" style={{ color: SLATE }}>
                  {p.category} · {p.country_name_fr}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {mine && mine.count > 0 && (
        <Card className="p-5" testid="marketplace-mine">
          <div className="text-[10px] uppercase tracking-widest mb-2" style={{ color: CHAMPAGNE }}>
            Mes soumissions · {mine.count}
          </div>
          <div className="space-y-2">
            {mine.plugins.map(p => (
              <div key={p.id} className="rounded-lg p-3 flex items-center gap-3"
                   style={{ background: "#F8FAFC" }}
                   data-testid={`marketplace-mine-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold" style={{ color: NAVY }}>{p.label_fr}</div>
                  <div className="text-[11px]" style={{ color: SLATE }}>
                    {p.category} · {p.country_name_fr}
                  </div>
                </div>
                <Pill tone={p.status === "approved" ? "green" :
                                p.status === "rejected" ? "red" :
                                p.activated ? "green" : "amber"}>
                  {p.status === "approved" ? (p.activated ? "Actif" : "Approuvé") :
                    p.status === "rejected" ? "Refusé" : "En attente"}
                </Pill>
              </div>
            ))}
          </div>
        </Card>
      )}

      {pending && (
        <Card className="p-5" testid="marketplace-admin">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={16} style={{ color: CHAMPAGNE }} />
            <h3 className="font-serif text-lg" style={{ color: NAVY }}>Validation admin</h3>
            <span className="text-[10px] uppercase tracking-widest ml-auto"
                  style={{ color: CHAMPAGNE }}>
              {pending.count} en attente
            </span>
          </div>
          {pending.plugins.length === 0 ? (
            <div className="text-[12px] italic" style={{ color: SLATE }}>
              Aucune soumission en attente.
            </div>
          ) : (
            <div className="space-y-2">
              {pending.plugins.map(p => (
                <div key={p.id} className="rounded-lg p-3 border"
                     style={{ borderColor: `${CHAMPAGNE}33` }}
                     data-testid={`marketplace-admin-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
                        {p.label_fr}
                      </div>
                      <div className="text-[11px]" style={{ color: SLATE }}>
                        {p.category} · {p.country_name_fr} · {p.submitter_name} ({p.submitter_org || "solo"})
                      </div>
                      <div className="text-[12px] mt-1" style={{ color: NAVY }}>{p.scope_fr}</div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <PrimaryButton onClick={() => review(p.id, "approve")}
                                        testid={`marketplace-approve-${p.id}`}>
                        Approuver
                      </PrimaryButton>
                      <GhostButton onClick={() => review(p.id, "reject")}
                                      testid={`marketplace-reject-${p.id}`}>
                        Refuser
                      </GhostButton>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function MarketplaceSubmitForm({ onCreated }) {
  const [f, setF] = useState({
    label_fr: "", category: "registry", country_code: "CA-QC",
    country_name_fr: "Québec (Canada)", scope_fr: "",
    submitter_name: "", submitter_email: "", submitter_org: "",
    legal_basis_fr: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  const submit = async () => {
    if (!f.label_fr || !f.scope_fr || !f.submitter_name || !f.submitter_email
        || !f.legal_basis_fr) {
      toast.error("Tous les champs marqués d'un astérisque sont requis."); return;
    }
    setBusy(true);
    try {
      await api.post("/intel/marketplace/submit", f);
      toast.success("Soumission envoyée à ALTIORA pour validation.");
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur.");
    }
    setBusy(false);
  };

  return (
    <div className="rounded-lg p-3 mt-3" style={{ background: IVORY }}
         data-testid="marketplace-submit-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
        <input value={f.label_fr} onChange={e => set("label_fr", e.target.value)}
                 placeholder="Nom du connecteur *" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <select value={f.category} onChange={e => set("category", e.target.value)}
                  className="p-2 rounded border" style={{ borderColor: CHAMPAGNE + "55" }}>
          {["registry","social","government","partner","bank","university","community","other"]
            .map(o => <option key={o}>{o}</option>)}
        </select>
        <input value={f.country_code} onChange={e => set("country_code", e.target.value)}
                 placeholder="Code pays (ISO 3166) *" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <input value={f.country_name_fr} onChange={e => set("country_name_fr", e.target.value)}
                 placeholder="Nom du pays *" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <textarea value={f.scope_fr} onChange={e => set("scope_fr", e.target.value)}
                    placeholder="Portée du connecteur *" rows={2}
                    className="md:col-span-2 p-2 rounded border resize-none"
                    style={{ borderColor: CHAMPAGNE + "55" }} />
        <input value={f.submitter_name} onChange={e => set("submitter_name", e.target.value)}
                 placeholder="Nom du soumetteur *" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <input value={f.submitter_email} onChange={e => set("submitter_email", e.target.value)}
                 placeholder="Courriel *" type="email" className="p-2 rounded border"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <input value={f.submitter_org} onChange={e => set("submitter_org", e.target.value)}
                 placeholder="Organisation" className="p-2 rounded border md:col-span-2"
                 style={{ borderColor: CHAMPAGNE + "55" }} />
        <textarea value={f.legal_basis_fr} onChange={e => set("legal_basis_fr", e.target.value)}
                    placeholder="Base légale (RGPD, données ouvertes…) *" rows={2}
                    className="md:col-span-2 p-2 rounded border resize-none"
                    style={{ borderColor: CHAMPAGNE + "55" }} />
      </div>
      <PrimaryButton onClick={submit} disabled={busy} testid="marketplace-submit-btn"
                        className="mt-3">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
        Soumettre pour validation
      </PrimaryButton>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   ANALYTICS & IMPACT — Iter 96
   ══════════════════════════════════════════════════════════════════ */
function AnalyticsPanel() {
  const [reg, setReg]         = useState(null);
  const [impact, setImpact]   = useState(null);
  const [digest, setDigest]   = useState(null);
  const [busy, setBusy]       = useState(false);

  const loadAll = async () => {
    setBusy(true);
    try {
      const [r, i, d] = await Promise.all([
        api.get("/intel/analytics/registries"),
        api.get("/intel/analytics/institutional-impact"),
        api.get("/intel/analytics/learning-digest"),
      ]);
      setReg(r.data); setImpact(i.data); setDigest(d.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les analyses.");
    }
    setBusy(false);
  };
  useEffect(() => { loadAll(); }, []);

  if (busy && !reg) {
    return <div className="p-6"><Loader2 size={16} className="animate-spin" /></div>;
  }

  // Formatters
  const fmt$ = (n) => (n || 0).toLocaleString("fr-CA");
  const REG_MAX = Math.max(1, ...(reg?.rows || []).map(r => r.total));

  return (
    <div className="space-y-4" data-testid="analytics-panel">
      {/* ── Headline metrics ────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Syncs registre"
                  value={reg?.total_syncs ?? 0}
                  hint="30 derniers jours"
                  testid="analytics-metric-syncs" />
        <Metric label="Contacts importés"
                  value={reg?.total_imported ?? 0}
                  accent={GREEN}
                  testid="analytics-metric-imported" />
        <Metric label="Dollars débloqués"
                  value={`${fmt$(impact?.total_dollars_unlocked)} $`}
                  accent={CHAMPAGNE}
                  hint="Impact institutionnel cumulé"
                  testid="analytics-metric-dollars" />
        <Metric label="Vies touchées"
                  value={impact?.total_lives_touched ?? 0}
                  accent={NAVY}
                  hint="Bénéficiaires estimés"
                  testid="analytics-metric-lives" />
      </div>

      {/* ── Live Registry Analytics ─────────────────────────────────── */}
      <Card className="p-5" testid="analytics-registries">
        <Section icon={BarChart3} title="Registres actifs · classement"
                    hint="Volume de contacts importés et mis à jour par registre. Les registres créés via l'assistant sans-code apparaissent automatiquement."
                    actions={[
                      <GhostButton key="r" onClick={loadAll} testid="analytics-refresh-btn">
                        <RefreshCw size={12} /> Actualiser
                      </GhostButton>,
                    ]}>
          {!reg || reg.rows.length === 0 ? (
            <div className="text-[12px] italic mt-3" style={{ color: SLATE }}>
              Aucun sync enregistré. Lancez une recherche depuis l'onglet Registres.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {reg.rows.slice(0, 10).map(r => {
                const pct = Math.round((r.total / REG_MAX) * 100);
                return (
                  <div key={r.registry_id}
                          className="p-2 rounded-lg"
                          style={{ background: "#F8FAFC" }}
                          data-testid={`analytics-registry-row-${r.registry_id}`}>
                    <div className="flex items-center gap-2 text-[12px] mb-1">
                      <span className="font-semibold" style={{ color: NAVY }}>
                        {r.registry_id}
                      </span>
                      <span className="text-[10px]" style={{ color: SLATE }}>
                        · {r.sync_count} sync(s)
                        {r.last_sync_at && <> · dernier {new Date(r.last_sync_at).toLocaleDateString("fr-CA")}</>}
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <Pill tone="green">+{r.total_imported}</Pill>
                        <Pill tone="gold">~{r.total_updated}</Pill>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden"
                            style={{ background: "#E5E7EB" }}>
                      <div className="h-full rounded-full transition-all"
                              style={{ width: `${pct}%`, background: CHAMPAGNE }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </Card>

      {/* ── Institutional Impact ────────────────────────────────────── */}
      <Card className="p-5" testid="analytics-impact">
        <Section icon={HeartHandshake} title="Rapport d'impact · Portail institutionnel"
                    hint="Chaque demande institutionnelle est reliée aux opportunités générées : combien de dollars ont été débloqués, combien de vies touchées.">
          {!impact || impact.cards.length === 0 ? (
            <div className="text-[12px] italic mt-3" style={{ color: SLATE }}>
              Aucune demande institutionnelle en cours pour votre profil.
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {impact.cards.slice(0, 8).map(c => (
                <div key={c.id}
                        className="rounded-lg p-3 border"
                        style={{ borderColor: `${CHAMPAGNE}33`, background: "#fff" }}
                        data-testid={`analytics-impact-card-${c.id}`}>
                  <div className="flex items-start gap-3">
                    <Building2 size={14} style={{ color: NAVY }} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
                        {c.title_fr}
                      </div>
                      <div className="text-[11px]" style={{ color: SLATE }}>
                        {c.institution_name} · statut {c.status} · urgence {c.urgency}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[12px] font-semibold flex items-center gap-1 justify-end"
                              style={{ color: CHAMPAGNE }}>
                        <DollarSign size={11} /> {fmt$(c.dollars_unlocked)} $
                      </div>
                      <div className="text-[11px]" style={{ color: NAVY }}>
                        {c.lives_touched} vies · {c.match_count} match(s)
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </Card>

      {/* ── Learning Digest ─────────────────────────────────────────── */}
      <Card className="p-5" testid="analytics-digest">
        <Section icon={Mail} title="Digest Copilote · aperçu de la semaine"
                    hint="Ce que votre Copilote a appris ces 7 derniers jours. Un digest hebdomadaire est envoyé chaque lundi matin par courriel.">
          {!digest ? null : (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg p-3"
                        style={{ background: `${GREEN}0A`, border: `1px solid ${GREEN}22` }}
                        data-testid="analytics-digest-events">
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: GREEN }}>
                    Arbitrages enregistrés
                  </div>
                  <div className="font-serif text-2xl font-bold mt-1" style={{ color: NAVY }}>
                    {digest.week_events}
                  </div>
                </div>
                <div className="rounded-lg p-3"
                        style={{ background: `${CRIMSON}0A`, border: `1px solid ${CRIMSON}22` }}
                        data-testid="analytics-digest-silence">
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: CRIMSON }}>
                    Silences pénalisés
                  </div>
                  <div className="font-serif text-2xl font-bold mt-1" style={{ color: NAVY }}>
                    {digest.silent_penalties}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div data-testid="analytics-digest-boosted">
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: GREEN }}>
                    <TrendingUp size={10} className="inline mb-0.5" /> Amplifiées
                  </div>
                  {digest.top_boosted.length === 0 ? (
                    <div className="text-[11px] italic" style={{ color: SLATE }}>
                      Aucune préférence forte détectée.
                    </div>
                  ) : digest.top_boosted.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12px] py-0.5">
                      <Pill tone="green">+{b.delta}</Pill>
                      <span style={{ color: NAVY }}>{b.value}</span>
                      <span className="text-[10px]" style={{ color: SLATE }}>· {b.feature}</span>
                    </div>
                  ))}
                </div>
                <div data-testid="analytics-digest-penalised">
                  <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: CRIMSON }}>
                    <TrendingDown size={10} className="inline mb-0.5" /> Atténuées
                  </div>
                  {digest.top_penalised.length === 0 ? (
                    <div className="text-[11px] italic" style={{ color: SLATE }}>
                      Aucune atténuation cette semaine.
                    </div>
                  ) : digest.top_penalised.map((b, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12px] py-0.5">
                      <Pill tone="red">{b.delta}</Pill>
                      <span style={{ color: NAVY }}>{b.value}</span>
                      <span className="text-[10px]" style={{ color: SLATE }}>· {b.feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-[10px] italic pt-2 border-t"
                      style={{ color: SLATE, borderColor: `${CHAMPAGNE}22` }}>
                Envoi automatique · lundi matin · Cette semaine :
                {" "}{digest.top_boosted.length + digest.top_penalised.length} ajustement(s) proposé(s).
              </div>
            </div>
          )}
        </Section>
      </Card>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN PAGE — Navigateur Économique Intelligent
   ══════════════════════════════════════════════════════════════════ */
export default function NavigateurEconomique() {
  const nav = useNavigate();
  const [tab, setTab] = useState("sync");

  const tabs = [
    { id: "sync",         label: "Santé",        icon: Radio },
    { id: "map",          label: "Carte",        icon: MapIcon },
    { id: "registries",   label: "Registres",    icon: Globe },
    { id: "institutions", label: "Institutions", icon: Building2 },
    { id: "learning",     label: "Apprentissage", icon: Brain },
    { id: "framework",    label: "Connecteurs",  icon: Puzzle },
    { id: "marketplace",  label: "Marketplace",  icon: Sparkles },
    { id: "analytics",    label: "Analytics",    icon: BarChart3 },
  ];

  return (
    <PageShell testid="nav-eco-root">
      <TopBar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-16">
        <PageHeader
          eyebrow="ALTIORA · Navigateur Économique"
          title="Le moteur qui centralise, qualifie et relie"
          subtitle="Une seule interface pour piloter la santé des connecteurs, cartographier votre territoire, explorer les registres publics internationaux, orchestrer le portail institutionnel et voir ce que votre IA a appris."
          onBack={() => nav("/app/intel-commerciale")}
          testid="nav-eco-header" />

        <TabsShell tabs={tabs} active={tab} onChange={setTab}
                     testid="nav-eco-tab" />

        {tab === "sync"         && <SyncDashboard />}
        {tab === "map"          && <FieldMap />}
        {tab === "registries"   && <RegistriesPanel />}
        {tab === "institutions" && <InstitutionalPortalPanel />}
        {tab === "learning"     && <LearningLoopPanel />}
        {tab === "framework"    && <ConnectorsFrameworkPanel />}
        {tab === "marketplace"  && <MarketplacePanel />}
        {tab === "analytics"    && <AnalyticsPanel />}
      </div>
    </PageShell>
  );
}
